import { IDockviewHeaderActionsProps } from "dockview";
import * as React from "react";
import {
  BsGear,
  BsPlus,
  BsQuestion,
  BsWindow,
  BsArrowClockwise,
  BsArrowLeft,
  BsClockHistory,
  BsArrowsFullscreen,
  BsFullscreenExit,
} from "react-icons/bs";
import * as styles from "./style.module.css";
import { observer } from "mobx-react-lite";
import { Popover, Form, OverlayTrigger, Spinner } from "react-bootstrap";
import I18n from "lib/I18n";
import { useEffect, useState } from "react";
import { ChatHistory, getStore } from "bundles/BigqueryQueryViewer/store";
import { showChat, showExplorer, showJob, showProblem } from "./common";
import { convertDurationToReadable } from "lib/converter";
import { ChatPreferencesModal } from "../Chat/preferences";

const windowIds = ["explorer", "problem", "job", "chat"] as const;
const windowShows = {
  explorer: showExplorer,
  problem: showProblem,
  job: showJob,
  chat: showChat,
} as const;

const EditorRightControls = observer((props: IDockviewHeaderActionsProps) => {
  const [showPreferences, setShowPreferences] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const store = getStore();
  const preferences = store.preferences;

  const isWindowVisible = (windowId: string) => {
    return props.containerApi.panels.some((_) => _.id == windowId);
  };

  const [windowVisibilities, setWindowVisibilities] = useState(
    windowIds.map(
      (_) => [_, isWindowVisible(_)] as [(typeof windowIds)[number], boolean],
    ),
  );

  useEffect(() => {
    const updateVisibilities = () => {
      setWindowVisibilities(
        windowIds.map(
          (_) =>
            [_, isWindowVisible(_)] as [(typeof windowIds)[number], boolean],
        ),
      );
    };
    props.containerApi.onDidAddPanel(updateVisibilities);
    props.containerApi.onDidRemovePanel(updateVisibilities);
  }, []);

  const onToggleFullscreen = () => {
    const dockviewRoot: HTMLElement | null =
      document.querySelector("#dockview-root");
    if (isFullscreen) {
      dockviewRoot?.style.removeProperty("position");
      dockviewRoot?.style.removeProperty("width");
      dockviewRoot?.style.removeProperty("top");
      dockviewRoot?.style.removeProperty("left");
      // 一旦heightを0にしてから元に戻してheight: 100%を再計算させる
      dockviewRoot?.style.setProperty("height", "0px");
      setTimeout(() => {
        dockviewRoot?.style.removeProperty("height");
      }, 0);
    } else {
      dockviewRoot?.style.setProperty("position", "absolute");
      dockviewRoot?.style.setProperty("height", "100%");
      dockviewRoot?.style.setProperty("width", "100%");
      dockviewRoot?.style.setProperty("top", "0");
      dockviewRoot?.style.setProperty("left", "0");
    }
    setIsFullscreen(!isFullscreen);
  };

  const preferencesOverlay = () => {
    return (
      <Popover className="p-2">
        <Form.Check
          checked={preferences.showReservedWordsAsLowerCase}
          onChange={(e) =>
            (preferences.showReservedWordsAsLowerCase = e.currentTarget.checked)
          }
          label={I18n.t(
            "bigquery_query_viewer.label.show_reserved_words_as_lower_case",
          )}
          id="preference-show-reserved-word-as-lower-case"
        />
        <div>
          <Form.Check
            checked={preferences.bigqueryUserDefinedThresholdEnabled}
            onChange={(e) =>
              (preferences.bigqueryUserDefinedThresholdEnabled =
                e.currentTarget.checked)
            }
            label={I18n.t(
              "bigquery_query_viewer.label.bigquery_user_defined_threshold_enabled",
            )}
            id="preference-bigquery-user-defined-threshold-enabled"
          />
          <div style={{ marginLeft: 30 }}>
            <Form.Control
              style={{
                width: 100,
                display: "inline-block",
                marginRight: 10,
                textAlign: "right",
              }}
              type="number"
              value={preferences.bigqueryUserDefinedThreshold}
              disabled={!preferences.bigqueryUserDefinedThresholdEnabled}
              onChange={(e) =>
                (preferences.bigqueryUserDefinedThreshold = parseInt(
                  e.currentTarget.value,
                  10,
                ))
              }
            />
            GB
          </div>
          {preferences.bigqueryAICompletionToSConsent && (
            <Form.Check
              checked={preferences.bigqueryAICompletionEnabled}
              onChange={(e) => {
                if (preferences.bigqueryAICompletionToSConsent) {
                  preferences.bigqueryAICompletionEnabled =
                    e.currentTarget.checked;
                }
              }}
              label={I18n.t(
                "bigquery_query_viewer.label.bigquery_ai_features_enabled",
              )}
              id="preference-bigquery-ai-completion-enabled"
            />
          )}
        </div>
      </Popover>
    );
  };

  const windowOverlay = () => {
    return (
      <Popover className="p-2">
        {windowVisibilities
          .filter(
            ([windowId, _]) =>
              windowId != "chat" || preferences.bigqueryAICompletionEnabled,
          )
          .map(([windowId, isVisible]) => (
            <Form.Check
              checked={isVisible}
              onChange={(e) => {
                if (e.currentTarget.checked) {
                  windowShows[windowId](props.containerApi);
                } else {
                  const panel = props.containerApi.panels.find(
                    (_) => _.id == windowId,
                  );
                  if (panel) {
                    props.containerApi.removePanel(panel);
                  }
                }
              }}
              label={I18n.t(`bigquery_query_viewer.label.${windowId}`)}
              id={`window-visibility-${windowId}`}
            />
          ))}
      </Popover>
    );
  };

  const onShowHelp = () => {
    const helpPanel = props.containerApi.panels.find((_) => _.id == "help");
    if (helpPanel) {
      helpPanel.focus();
      return;
    }
    props.containerApi.addPanel({
      id: "help",
      component: "help",
      title: I18n.t("nav.help"),
      position: {
        referenceGroup: "editors",
      },
    });
  };

  return (
    <>
      <div className={styles.groupControl}>
        <OverlayTrigger
          trigger="click"
          placement="bottom"
          overlay={windowOverlay()}
        >
          <div
            className={styles.action}
            title={I18n.t("bigquery_query_viewer.label.window")}
          >
            <BsWindow />
          </div>
        </OverlayTrigger>
        <div
          className={styles.action}
          title={
            isFullscreen
              ? I18n.t("bigquery_query_viewer.label.minimize")
              : I18n.t("bigquery_query_viewer.label.maximize")
          }
          onClick={onToggleFullscreen}
        >
          {isFullscreen ? <BsFullscreenExit /> : <BsArrowsFullscreen />}
        </div>
        <OverlayTrigger
          trigger="click"
          placement="bottom"
          show={showPreferences}
          onToggle={(show) => setShowPreferences(show)}
          overlay={preferencesOverlay()}
        >
          <div
            className={styles.action}
            title={I18n.t("bigquery_query_viewer.label.config")}
          >
            <BsGear />
          </div>
        </OverlayTrigger>
        <div
          className={styles.action}
          title={I18n.t("bigquery_query_viewer.label.help")}
          onClick={onShowHelp}
        >
          <BsQuestion />
        </div>
      </div>
    </>
  );
});

const ChatRightControls = observer(() => {
  const store = getStore();

  const [showHisotry, setShowHistory] = useState(false);

  useEffect(() => {
    if (!showHisotry) {
      return;
    }
    store.chat.listChatHistories();
  }, [showHisotry]);

  const onSelectHistory = (history: ChatHistory) => {
    if (store.chat.isSessionSwitchBlocked) {
      return;
    }
    store.chat.restoreSession(history.sessionKey);
  };

  const historyOverlay = () => {
    const now = new Date();

    return (
      <Popover className="p-2">
        {store.chat.isChatHistoryLoading ? (
          <Spinner animation="border" size="sm" />
        ) : (
          <div>
            {store.chat.chatHistories.length == 0
              ? I18n.t("bigquery_query_viewer.messages.no_chat_history")
              : store.chat.chatHistories.map((history) => {
                  const lastUseTime = new Date();
                  lastUseTime.setTime(history.lastUseTime * 1000);
                  return (
                    <button
                      type="button"
                      key={history.sessionKey}
                      className={styles.history}
                      disabled={store.chat.isSessionSwitchBlocked}
                      onClick={() => onSelectHistory(history)}
                    >
                      {I18n.t("bigquery_query_viewer.label.last_use", {
                        time: convertDurationToReadable(
                          Math.ceil(
                            (now.getTime() - lastUseTime.getTime()) / 1000,
                          ),
                        ),
                      })}
                      {history.sessionKey == store.chat.currentSessionKey &&
                        "(現在)"}
                    </button>
                  );
                })}
          </div>
        )}
      </Popover>
    );
  };

  const onRevertChatContext = () => {
    store.chat.revert();
  };

  const onRefreshChatContext = () => {
    store.chat.refresh();
  };

  return (
    <>
      <div className={styles.groupControl}>
        <button
          title={I18n.t("bigquery_query_viewer.label.undo_chat")}
          className={styles.action}
          disabled={
            !store.preferences.bigqueryAICompletionEnabled ||
            store.chat.isSessionSwitchBlocked
          }
          onClick={onRevertChatContext}
        >
          <BsArrowLeft />
        </button>
        <button
          title={I18n.t("bigquery_query_viewer.label.refresh_chat")}
          className={styles.action}
          disabled={
            !store.preferences.bigqueryAICompletionEnabled ||
            store.chat.isSessionSwitchBlocked
          }
          onClick={onRefreshChatContext}
        >
          <BsArrowClockwise />
        </button>
        <OverlayTrigger
          trigger="click"
          placement="left"
          show={showHisotry}
          onToggle={(show) => setShowHistory(show)}
          overlay={historyOverlay()}
        >
          <button
            title={I18n.t("bigquery_query_viewer.label.chat_history")}
            className={styles.action}
            disabled={
              !store.preferences.bigqueryAICompletionEnabled ||
              store.chat.isSessionSwitchBlocked
            }
          >
            <BsClockHistory />
          </button>
        </OverlayTrigger>
        <button
          title={I18n.t("bigquery_query_viewer.label.chat_preferences")}
          className={styles.action}
          disabled={!store.preferences.bigqueryAICompletionEnabled}
          onClick={() => store.chat.openPreferences()}
        >
          <BsGear />
        </button>
      </div>
      <ChatPreferencesModal />
    </>
  );
});

// editorsパネルグループのタブの右側のコントロール
export const RightControls = observer((props: IDockviewHeaderActionsProps) => {
  const isEditorGroup = props.group.id == "editors";
  const isChatPanel = !!props.panels.find((_) => _.id == "chat");
  if (isEditorGroup) {
    return <EditorRightControls {...props} />;
  }
  if (isChatPanel) {
    return <ChatRightControls />;
  }
  return <div></div>;
});

// editorsパネルグループのタブ一覧の最後のコントロール
export const LeftControls = observer((props: IDockviewHeaderActionsProps) => {
  const isEditorGroup = props.group.id == "editors";

  const store = getStore();

  const onClick = () => {
    // App/index.tsx側でdockviewには同期される
    store.createFormStore();
  };

  return (
    isEditorGroup && (
      <div className={styles.groupControl}>
        <div className={styles.action} onClick={onClick}>
          <BsPlus />
        </div>
      </div>
    )
  );
});
